import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import type { PredictionEventRef } from '@src/types/PredictionMarket';
import { isPredictionMarketEventTradable } from '@src/types/PredictionMarket';
import {
  GET,
  request,
  useSecuritiesContext,
  useUserContext,
  type IPredictionOutcomeEvent,
  type IPredictionOutcomeEventSeriesRecord,
  type Response,
  type Security,
} from '@talos/kyoko';
import { useQuery } from '@tanstack/react-query';
import { createContext, memo, useCallback, useContext, useMemo, useRef, type PropsWithChildren } from 'react';
import {
  buildPredictionRefDataIndexes,
  EMPTY_PREDICTION_REF_DATA_INDEXES,
  getIndexedOutcomesForPredictionEvent,
  type PredictionRefDataIndexes,
} from './predictionRefDataIndexes';

type PredictionsContextValue = {
  events: IPredictionOutcomeEvent[];
  /** Events that are open for trading and selection. */
  selectableEvents: IPredictionOutcomeEvent[];
  eventsByID: Map<string, IPredictionOutcomeEvent>;
  eventSeries: IPredictionOutcomeEventSeriesRecord[];
  eventSeriesByID: Map<string, IPredictionOutcomeEventSeriesRecord>;
  /** Shared market/event/outcome indexes. */
  refDataIndexes: PredictionRefDataIndexes;
  /**
   * True once the securities stream and the events/series queries have loaded for the current org,
   * and stays true while the queries refetch in the background. Cards and selectors wait on this so
   * they show a loading state rather than an empty one while indexes are still being built.
   */
  isRefDataReady: boolean;
  getOutcomesForPredictionEvent: (
    event: PredictionEventRef | Pick<IPredictionOutcomeEvent, 'EventID' | 'EventSymbol' | 'EventName'>
  ) => Security[];
};

export const PredictionsContext = createContext<PredictionsContextValue | undefined>(undefined);
PredictionsContext.displayName = 'PredictionsContext';

export function usePredictionsContext(): PredictionsContextValue {
  const context = useContext(PredictionsContext);
  if (context === undefined) {
    throw new Error('Missing PredictionsContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

export function useOptionalPredictionsContext(): PredictionsContextValue | undefined {
  return useContext(PredictionsContext);
}

export const PredictionsProvider = memo(function PredictionsProvider({ children }: PropsWithChildren) {
  const { orgApiEndpoint } = useUserContext();
  const {
    isLoaded: isSecuritiesLoaded,
    predictionOutcomesByEventID,
    predictionSupportedMarkets,
  } = useSecuritiesContext();
  const { enablePredictionMarkets } = useFeatureFlag();
  const eventsEndpoint = `${orgApiEndpoint}/prediction-outcome-events`;
  const eventSeriesEndpoint = `${orgApiEndpoint}/prediction-outcome-event-series`;

  const eventsQuery = useQuery({
    queryKey: ['predictionEvents', orgApiEndpoint],
    queryFn: () =>
      request<Response<IPredictionOutcomeEvent>>(GET, eventsEndpoint).then(response => ({
        events: response.data,
        selectableEvents: response.data.filter(isPredictionMarketEventTradable),
      })),
    enabled: enablePredictionMarkets && !!orgApiEndpoint,
    staleTime: 60_000,
  });

  const eventSeriesQuery = useQuery({
    queryKey: ['predictionEventSeries', orgApiEndpoint],
    queryFn: () => request<Response<IPredictionOutcomeEventSeriesRecord>>(GET, eventSeriesEndpoint),
    enabled: enablePredictionMarkets && !!orgApiEndpoint,
    staleTime: 60_000,
  });

  const { events, selectableEvents } = useMemo(
    () => eventsQuery.data ?? { events: [], selectableEvents: [] },
    [eventsQuery.data]
  );

  const eventsByID = useMemo(() => new Map(events.map(event => [event.EventID, event])), [events]);

  const eventSeries = useMemo(() => eventSeriesQuery.data?.data ?? [], [eventSeriesQuery.data]);

  const eventSeriesByID = useMemo(() => new Map(eventSeries.map(series => [series.SeriesID, series])), [eventSeries]);

  // Retained so each rebuild can reuse the derivations for events whose outcomes did not change.
  const previousRefDataIndexes = useRef<PredictionRefDataIndexes>(EMPTY_PREDICTION_REF_DATA_INDEXES);

  const refDataIndexes = useMemo(() => {
    if (!enablePredictionMarkets) {
      return EMPTY_PREDICTION_REF_DATA_INDEXES;
    }
    const indexes = buildPredictionRefDataIndexes(
      { predictionOutcomesByEventID, predictionSupportedMarkets },
      events,
      previousRefDataIndexes.current
    );
    previousRefDataIndexes.current = indexes;
    return indexes;
  }, [enablePredictionMarkets, events, predictionOutcomesByEventID, predictionSupportedMarkets]);

  // Outcome indexes are keyed off the securities stream, so events alone are not enough to tell an
  // empty event from one whose outcomes have not streamed in yet.
  // isFetched stays true across background refetches, so an open card form keeps its options
  // instead of collapsing back to the loading state. It resets per org because the query key does.
  const isRefDataReady =
    !enablePredictionMarkets || (isSecuritiesLoaded && eventsQuery.isFetched && eventSeriesQuery.isFetched);

  const getOutcomesForPredictionEvent = useCallback(
    (event: PredictionEventRef | Pick<IPredictionOutcomeEvent, 'EventID' | 'EventSymbol' | 'EventName'>) => {
      return getIndexedOutcomesForPredictionEvent(event, refDataIndexes);
    },
    [refDataIndexes]
  );

  const value = useMemo(
    (): PredictionsContextValue => ({
      events,
      selectableEvents,
      eventsByID,
      eventSeries,
      eventSeriesByID,
      refDataIndexes,
      isRefDataReady,
      getOutcomesForPredictionEvent,
    }),
    [
      events,
      selectableEvents,
      eventsByID,
      eventSeries,
      eventSeriesByID,
      refDataIndexes,
      isRefDataReady,
      getOutcomesForPredictionEvent,
    ]
  );

  return <PredictionsContext.Provider value={value}>{children}</PredictionsContext.Provider>;
});
